import { rumSessionIdYieldAttributes, trackAgentChat } from '@circuitry-ai/doc-ui';

export interface AgentStreamChunk {
    event?: string;
    workflow_run_id?: string;
    task_id?: string;
    data?: {
        id?: string;
        node_id?: string;
        node_type?: string;
        title?: string;
        index?: number;
        status?: string;
        elapsed_time?: number;
        outputs?: any;
        text?: string;
        from_variable_selector?: string[];
        [key: string]: any;
    };
    [key: string]: any;
}

export type ChunkHandler = (chunk: AgentStreamChunk) => void;

function parseSSELines(buffer: string, onData: (data: any) => void) {
    const lines = buffer.split(/\n+/).filter(Boolean);
    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const payloadStr = trimmed.slice(5).trim();
        if (!payloadStr) continue;
        try {
            const parsed = JSON.parse(payloadStr);

            // Handle Dify API structure (direct JSON)
            if (parsed.event) {
                onData(parsed);
            }
        } catch {
            // ignore malformed lines
        }
    }
}

async function readStream(response: Response, onChunk: ChunkHandler, onComplete?: () => void, onError?: (e: Error) => void) {
    try {
        const reader = response.body?.getReader();
        if (!reader) throw new Error('ReadableStream not supported');
        const decoder = new TextDecoder();
        let pending = '';
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const text = decoder.decode(value, { stream: true });
            pending += text;
            // Process complete lines, keep any partial at the end
            const lastNewline = pending.lastIndexOf('\n');
            if (lastNewline >= 0) {
                const chunk = pending.slice(0, lastNewline + 1);
                pending = pending.slice(lastNewline + 1);
                parseSSELines(chunk, onChunk);
            }
        }
        if (pending) {
            parseSSELines(pending, onChunk);
        }
        onComplete?.();
    } catch (e) {
        onError?.(e as Error);
    }
}

// Track in-flight start-stream requests to prevent duplicates
const inflightStartStream: Map<string, {
    promise: Promise<{ session_id?: string }>,
    abort: AbortController
}> = new Map();

function makeStartKey(baseUrl: string | undefined, aiworker_id: string, token: string) {
    return `${baseUrl || ''}::${aiworker_id}::${token}`;
}

export async function startAgentStream(params: {
    baseUrl?: string;
    aiworker_id: string;
    token: string;
    onChunk?: ChunkHandler;
    signal?: AbortSignal;
}): Promise<{ session_id?: string }> {
    const { baseUrl, aiworker_id, token, onChunk, signal } = params;
    const url = `${baseUrl}/start-stream`;
    const body = JSON.stringify({ payload: { aiworker_id } });

    const key = makeStartKey(baseUrl, aiworker_id, token);

    // If a request is already in-flight for this key, return the same promise
    const existing = inflightStartStream.get(key);
    if (existing) {
        return existing.promise;
    }

    // Create an abort controller that we can cancel if the caller aborts
    const controller = new AbortController();
    const forwardAbort = () => controller.abort();
    if (signal) {
        if (signal.aborted) controller.abort();
        else signal.addEventListener('abort', forwardAbort, { once: true });
    }

    const doFetch = async (): Promise<{ session_id?: string }> => {
        try {
            return await trackAgentChat({
                operation: 'start_stream',
                startAttributes: { aiworker_id },
                run: async () => {
                    const response = await fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'text/event-stream',
                            'Authorization': `Bearer ${token}`
                        },
                        body,
                        signal: controller.signal
                    });

                    if (!response.ok) throw new Error(`start-stream failed: ${response.status}`);

                    const contentType = response.headers.get('content-type') || '';
                    if (contentType.includes('application/json')) {
                        const jsonResponse = await response.json();
                        return { session_id: jsonResponse?.session_id };
                    }

                    let resolvedSession: string | undefined;
                    await readStream(
                        response,
                        (inner) => {
                            onChunk?.(inner);
                            if (!resolvedSession) {
                                const candidate = inner?.session_id || inner?.inputs?.session_id || inner?.metadata?.session_id;
                                if (typeof candidate === 'string' && candidate.length > 0) {
                                    resolvedSession = candidate;
                                }
                            }
                        },
                        undefined,
                        undefined
                    );
                    return { session_id: resolvedSession };
                },
                completeAttributes: (result) =>
                    rumSessionIdYieldAttributes(result.session_id),
            });
        } finally {
            if (signal) signal.removeEventListener('abort', forwardAbort as any);
            inflightStartStream.delete(key);
        }
    };

    const promise = doFetch();
    inflightStartStream.set(key, { promise, abort: controller });
    return promise;
}

export async function streamAgentChat(params: {
    baseUrl?: string;
    token: string;
    aiworker_id: string;
    session_id: string;
    user_input: string;
    source_id?: Record<string, string>;
    meta_data?: {
        is_edit: boolean;
        instructions?: string;
        image_source_id?: string;
        image_file_name?: string;
        original_data?: string;
        template_type?: 'html' | 'jsx';
        encoding?: 'base64';
        token?: string;
    };
    payloadFields?: Record<string, any>;
    onChunk: ChunkHandler;
    onComplete?: () => void;
    onError?: (e: Error) => void;
    customUrl?: string;
}): Promise<void> {
    const { baseUrl, token, aiworker_id, session_id, user_input, source_id = {}, meta_data, payloadFields, onChunk, onComplete, onError, customUrl } = params;

    let url: string;
    let method: 'GET' | 'POST';
    let body: string | undefined;

    if (customUrl) {
        // Use GET request for custom URL with query parameters
        const queryParams = new URLSearchParams({
            user_input,
            aiworker_id,
            session_id,
            ...(source_id && Object.keys(source_id).length > 0 ? { source_id: JSON.stringify(source_id) } : {})
        });
        url = `${customUrl}?${queryParams.toString()}`;
        method = 'GET';
        body = undefined;
    } else {
        // Use POST request for default URL with JSON body
        url = `${baseUrl}/dify_stream`;
        method = 'POST';
        const payloadBody = payloadFields
            ? { ...payloadFields, aiworker_id, session_id }
            : {
                user_input,
                aiworker_id,
                session_id,
                ...(source_id && Object.keys(source_id).length > 0 ? { source_id } : {}),
                ...(meta_data ? { meta_data } : {})
            };
        body = JSON.stringify({ payload: payloadBody });
    }

    await trackAgentChat({
        operation: 'dify_stream',
        startAttributes: {
            aiworker_id,
            session_id,
            has_custom_url: Boolean(customUrl),
            has_source_ids: Boolean(source_id && Object.keys(source_id).length > 0),
        },
        run: async () => {
            let lastMessageId: string | null = null;
            let sawWorkflowFinished = false;

            const trackedOnChunk: ChunkHandler = (chunk) => {
                onChunk(chunk);
                if (chunk?.event === 'workflow_finished') {
                    sawWorkflowFinished = true;
                    const outputs = chunk?.data?.outputs;
                    const candidate =
                        outputs?.message_id ??
                        chunk?.data?.message_id ??
                        outputs?.aiworker_usage_id;
                    if (typeof candidate === 'string' && candidate.trim()) {
                        lastMessageId = candidate.trim();
                    }
                }
            };

            try {
                const response = await fetch(url, {
                    method,
                    headers: {
                        ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
                        'Accept': customUrl ? 'application/json' : 'text/event-stream',
                        'Authorization': `Bearer ${token}`
                    },
                    ...(body ? { body } : {})
                });
                if (!response.ok) throw new Error(`dify stream failed: ${response.status}`);

                if (customUrl) {
                    const jsonResponse = await response.json();
                    onChunk(jsonResponse);
                    onComplete?.();
                    sawWorkflowFinished = true;
                } else {
                    await new Promise<void>((resolve, reject) => {
                        void readStream(
                            response,
                            trackedOnChunk,
                            () => {
                                onComplete?.();
                                resolve();
                            },
                            (err) => {
                                onError?.(err);
                                reject(err);
                            },
                        );
                    });
                }

                return {
                    message_id: lastMessageId,
                    saw_workflow_finished: sawWorkflowFinished,
                };
            } catch (e) {
                onError?.(e as Error);
                throw e;
            }
        },
        completeAttributes: (result) => ({
            message_id: result.message_id,
            data_yielded: result.saw_workflow_finished,
        }),
    });
}


